const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

const STORAGE_KEY = 'garden_shield_tracking'
const BASE_HOTMART_URL = 'https://pay.hotmart.com/B105054698I?checkoutMode=10'

// ─── Cookie Reader ───────────────────────────────────────
function getCookie(name: string): string {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? decodeURIComponent(match[2]) : ''
  } catch {
    return ''
  }
}

// ─── Capture UTMs + fbclid on page load ──────────────────
export function captureTrackingParams() {
  try {
    const params = new URLSearchParams(window.location.search)
    const stored = getStoredData()

    // Capture UTMs (overwrite if new ones present)
    let hasNewParams = false
    for (const key of UTM_KEYS) {
      const value = params.get(key)
      if (value) {
        stored[key] = value
        hasNewParams = true
      }
    }

    // Capture fbclid
    const fbclid = params.get('fbclid')
    if (fbclid) {
      stored.fbclid = fbclid
      hasNewParams = true
    }

    if (hasNewParams) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    }
  } catch {
    // Silent fail — never block page load
  }
}

// ─── Read stored tracking data ───────────────────────────
function getStoredData(): Record<string, string> {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

// ─── Read Meta cookies for CAPI ──────────────────────────
function getMetaCookies(): { fbp: string; fbc: string } {
  return {
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
  }
}

// ─── Build src/sck combined string ───────────────────────
// Formula: utm_source|utm_medium|utm_campaign|utm_content|utm_term
function buildSrcSck(data: Record<string, string>): string {
  return UTM_KEYS.map((key) => data[key] || '').join('|')
}

// ─── Build the full Hotmart URL with all tracking ────────
export function getHotmartUrl(): string {
  try {
    const data = getStoredData()
    const cookies = getMetaCookies()
    const url = new URL(BASE_HOTMART_URL)

    // Append UTMs
    for (const key of UTM_KEYS) {
      if (data[key]) {
        url.searchParams.set(key, data[key])
      }
    }

    // Append fbclid
    if (data.fbclid) {
      url.searchParams.set('fbclid', data.fbclid)
    }

    // Append src and sck (combined UTM string for Hotmart tracking)
    const srcSck = buildSrcSck(data)
    if (srcSck.replace(/\|/g, '')) {
      // Only add if there's actual data
      url.searchParams.set('src', srcSck)
      url.searchParams.set('sck', srcSck)
    }

    // Append Meta cookies for CAPI
    if (cookies.fbp) {
      url.searchParams.set('fbp', cookies.fbp)
    }
    if (cookies.fbc) {
      url.searchParams.set('fbc', cookies.fbc)
    }

    return url.toString()
  } catch {
    // Fallback: return base URL so user always reaches checkout
    return BASE_HOTMART_URL
  }
}

// ─── Click handler: track → redirect (with fallback) ─────
export function handleCheckoutClick(e: React.MouseEvent<HTMLAnchorElement>) {
  e.preventDefault()
  const checkoutUrl = getHotmartUrl()

  try {
    // Fire InitiateCheckout + AddToCart before redirect
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'The 365 Garden Shield',
        content_type: 'product',
        value: 6.37,
        currency: 'USD',
      })
      window.fbq('track', 'AddToCart', {
        content_name: 'The 365 Garden Shield',
        content_type: 'product',
        value: 6.37,
        currency: 'USD',
      })
    }

    // Small delay to allow pixel events to fire before navigation
    setTimeout(() => {
      window.open(checkoutUrl, '_blank', 'noopener,noreferrer')
    }, 300)
  } catch {
    // Fallback: always redirect even if tracking fails
    window.open(checkoutUrl, '_blank', 'noopener,noreferrer')
  }
}
